import {
  IStatementColumns,
  IStatementLine,
  StatementColumns,
  StatementLineType,
} from "shared";
import { ArrowDownTrayIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

import { classNames } from "../../../../common/lib/classNames";
import { DatePopover } from "../../../../common/Components/DatePopover";
import { SmallSpinner } from "../../../../common/Atoms/SmallSpinner";
import { formatAmount } from "../../../../common/lib/helpers";

import { isMoneyColumn } from "./StatementHeading";

export const creditBadges: Partial<Record<StatementLineType, { label: string; title: string; text: string; badge: string }>> = {
  [StatementLineType.CreditNote]: { label: `CR`, title: `Credit Note`, text: `text-orange-700`, badge: `border-orange-200 bg-orange-50` },
  [StatementLineType.Prepayment]: { label: `PP`, title: `Prepayment`, text: `text-blue-700`, badge: `border-blue-200 bg-blue-50` },
  [StatementLineType.Overpayment]: { label: `OP`, title: `Overpayment`, text: `text-green-700`, badge: `border-green-200 bg-green-50` },
};

export function isInvoiceLine(line: IStatementLine) {
  return line.type === StatementLineType.Invoice || line.type === StatementLineType.PaidInvoice;
}

export function AgePill({ days, suffix = `days` }: { days?: number; suffix?: string }) {
  if (!days || days <= 0) {
    return null;
  }

  return (
    <span className={ classNames(
      `whitespace-nowrap rounded-full px-2 py-px text-xs font-medium`,
      days > 30 ? `bg-paidniceRed-50 text-paidniceRed-700` : `bg-orange-50 text-orange-700`,
    ) }>
      { `${days} ${suffix}` }
    </span>
  );
}

export function SelectBox({ checked, onChange, disabled }: { checked: boolean; onChange: () => void; disabled?: boolean }) {
  return (
    <input
      type={ `checkbox` }
      aria-label={ `Select` }
      checked={ checked }
      disabled={ disabled }
      onClick={ e => e.stopPropagation() }
      onChange={ onChange }
      className={ `block h-4 w-4 cursor-pointer rounded border-gray-400 text-paidniceGreen-600 focus:ring-paidniceGreen-600 disabled:cursor-default disabled:border-gray-200 disabled:bg-gray-50` }
    />
  );
}

/** Display value for a statement column, other than the activity (invoice number) column */
export function statementCellValue(line: IStatementLine, type: string, organisationTimezone: string): React.ReactNode {
  const money = (cents: number) => formatAmount(cents, line.currency);

  switch (type) {
  case StatementColumns.Reference: return line.invoiceReference;
  case StatementColumns.Date: return (
    <DatePopover
      date={ line.issueDateString }
      organisationTimezone={ organisationTimezone }
      labelFormat={ `dd LLL yyyy` }
      disablePopover
    />
  );
  case StatementColumns.DueDate: return line.invoiceDueDateString && (
    <DatePopover
      date={ line.invoiceDueDateString }
      organisationTimezone={ organisationTimezone }
      labelFormat={ `dd LLL yyyy` }
      time={ `end` }
      disablePopover
    />
  );
  case StatementColumns.PaidDate: return line.paidDateString && (
    <DatePopover
      date={ line.paidDateString }
      organisationTimezone={ organisationTimezone }
      labelFormat={ `dd LLL yyyy` }
      disablePopover
    />
  );
  case StatementColumns.OverdueAge: return <AgePill days={ line.debtAgeInDays } />;
  case StatementColumns.Amount: return money(line.totalInCents);
  case StatementColumns.NetAmount: return money(line.subTotalInCents);
  case StatementColumns.Payments: return money(line.paymentsInCents);
  case StatementColumns.Balance: return money(line.outstandingInCents);
  case StatementColumns.Tax: return money(line.totalTaxInCents);
  case StatementColumns.QBOPrivateNote: return line.qboPrivateNote;
  case StatementColumns.PromptPaymentDiscountAmount:
    return line.totalPaidniceDiscountInCents ? money(line.totalPaidniceDiscountInCents) : ``;
  // Custom invoice field
  default: return line.childInvoiceFields.find(f => f.name === type)?.value || ``;
  }
}

const td = `whitespace-nowrap border-b border-gray-100 px-1.5 py-2.5 first:pl-2 last:pr-2`;

export const iconButton = `flex h-6 w-[1.125rem] cursor-pointer items-center justify-center rounded text-gray-500 hover:bg-paidniceGreen-50 hover:text-paidniceGreen-600`;

interface IStatementLineProps {
  line: IStatementLine;
  columnSettings: IStatementColumns;
  organisationTimezone: string;
  showPayColumn?: boolean;
  downloading?: boolean;
  onDownload: (line: IStatementLine) => void;
  onOpen: (line: IStatementLine) => void;
  onToggle: (line: IStatementLine) => void;
  selected: boolean;
}

export function StatementLine({
  line,
  columnSettings = [],
  organisationTimezone,
  showPayColumn,
  downloading,
  onDownload,
  onOpen,
  onToggle,
  selected,
}: IStatementLineProps) {
  const isInvoice = isInvoiceLine(line);
  const credit = creditBadges[line.type];

  function renderCell(type: string) {
    if (type !== StatementColumns.Activity) {
      return statementCellValue(line, type, organisationTimezone);
    }

    if (isInvoice) {
      return (
        <button
          type={ `button` }
          className={ `cursor-pointer font-medium text-paidniceGreen-600 hover:text-paidniceGreen-700 hover:underline` }
          onClick={ e => {
            e.stopPropagation();
            onOpen(line);
          } }
        >
          { line.invoiceNumber }
        </button>
      );
    }

    return (
      <span
        className={ classNames(`flex items-center gap-1.5`, credit?.text) }
        title={ credit?.title }
      >
        <span className={ `truncate` }>{ line.invoiceNumber }</span>
        { credit && (
          <span className={ classNames(`shrink-0 rounded border px-1 text-[0.6875rem] font-semibold`, credit.badge) }>{ credit.label }</span>
        ) }
      </span>
    );
  }

  return (
    <tr
      className={ classNames(
        `text-[0.8125rem] tabular-nums text-gray-700`,
        isInvoice && `cursor-pointer`,
        selected ? `bg-paidniceGreen-50` : `hover:bg-gray-50`,
      ) }
      onClick={ () => isInvoice && onToggle(line) }
    >
      <td className={ td }>
        <SelectBox
          checked={ selected }
          onChange={ () => onToggle(line) }
          disabled={ !isInvoice }
        />
      </td>

      { columnSettings.map((column, index) => {
        const isMoney = isMoneyColumn(column.type);
        const negative = isMoney && credit;

        return (
          <td
            key={ `column-${index}-${column.type}` }
            className={ classNames(
              td,
              isMoney && `text-right`,
              (column.type === StatementColumns.Reference || column.type === StatementColumns.Activity) && `max-w-[12rem] truncate`,
              negative && `text-orange-700`,
              !negative && column.type === StatementColumns.Balance && `font-medium text-gray-900`,
            ) }
          >
            { renderCell(column.type) }
          </td>
        );
      }) }

      { showPayColumn && (
        <td
          className={ td }
          onClick={ e => e.stopPropagation() }
        >
          { line.paymentLink && (
            <a
              href={ line.paymentLink }
              target={ `_blank` }
              rel={ `noopener noreferrer` }
              className={ `font-medium` }
            >
              { `Pay now` }
            </a>
          ) }
        </td>
      ) }

      <td
        className={ td }
        onClick={ e => e.stopPropagation() }
      >
        { isInvoice && (
          <span className={ `flex justify-end` }>
            <button
              type={ `button` }
              title={ `Download Invoice PDF` }
              className={ classNames(iconButton, downloading && `cursor-wait`) }
              onClick={ () => onDownload(line) }
              disabled={ downloading }
            >
              { downloading ? <SmallSpinner colored /> : <ArrowDownTrayIcon className={ `h-4 w-4` } /> }
            </button>
            <button
              type={ `button` }
              title={ `View Invoice Details` }
              className={ iconButton }
              onClick={ () => onOpen(line) }
            >
              <ChevronRightIcon className={ `h-4 w-4` } />
            </button>
          </span>
        ) }
      </td>
    </tr>
  );
}
